import React, { useState } from 'react';
import ReactDOM from 'react-dom';

import { Formik } from 'formik';
import * as Yup from 'yup';

import { DepartmentsBudgetsReportTableProvider } from './context/DepartmentsBudgetsReportTableContext';
import DepartmentsBudgetsReportTable from './DepartmentsBudgetsReportTable';

import { apiClient } from 'Helpers/network';

import Button from 'CommonComponents/Button';
import TextFormField from 'CommonComponents/TextFormField';

import { ToastProvider } from 'Context/ToastContext';

import useQuery from 'Hooks/useQuery';
import { useToast } from 'Hooks/useToast';

const ValidationSchema = Yup.object().shape({
    reviewer_name: Yup.string().max(255, 'Your name must be at most 255 characters').required('Your name is required'),
    comments: Yup.object()
        .test('comments-min', 'Please add at least one comment before submitting', (obj) => {
            if (!obj) return false;
            const nonEmpty = Object.values(obj).filter((v) => v && v.trim() !== '');
            return nonEmpty.length >= 1;
        })
        .test('comments-max-length', 'Each comment must be at most 1000 characters', (obj) => {
            if (!obj) return true;
            return Object.values(obj).every((v) => !v || v.length <= 1000);
        }),
});

const OnlineReview = ({ reviewDataUrl, reviewUpdateUrl, hasAnyReviewedActions }) => {
    const [success, setSuccess] = useState(false);

    const tost = useToast();

    const budgetsDataRequest = useQuery({
        queryFn: () => apiClient.get(reviewDataUrl),
    });

    if (success) {
        return (
            <div className="tw-p-8 tw-bg-green-50 tw-rounded-lg tw-text-green-900 tw-text-center">
                <h2 className="tw-text-2xl tw-font-bold tw-mb-4">Review submitted!</h2>
                <div>Thank you for your feedback. The sender will be notified.</div>
            </div>
        );
    }

    return (
        <Formik
            enableReinitialize
            initialValues={{
                comments: {},
                reviewer_name: budgetsDataRequest.data?.reviewer_name || '',
            }}
            validationSchema={ValidationSchema}
            onSubmit={(values, { setSubmitting }) => {
                apiClient
                    .put(reviewUpdateUrl, { comments: values.comments, reviewer_name: values.reviewer_name })
                    .then(() => {
                        setSuccess(true);
                        tost.success('Review submitted successfully.');
                    })
                    .catch(() => {
                        tost.error('Failed to submit review.');
                    })
                    .finally(() => {
                        setSubmitting(false);
                    });
            }}
        >
            {({ values, handleSubmit, isSubmitting, errors, isValid }) => (
                <div className="tw-flex tw-flex-col tw-space-y-6">
                    <DepartmentsBudgetsReportTableProvider
                        criteria={budgetsDataRequest.data?.criteria}
                        report={budgetsDataRequest.data?.report}
                        budgets={budgetsDataRequest.data?.budgets}
                        comments={values.comments}
                        allComments={budgetsDataRequest.data?.comments || {}}
                        userType={budgetsDataRequest.data?.user_type || 'reviewer'}
                        loading={budgetsDataRequest.loading}
                        showComments
                        editableComments
                    >
                        <DepartmentsBudgetsReportTable />
                    </DepartmentsBudgetsReportTableProvider>
                    {errors.comments && <div className="tw-text-red-600 tw-text-sm">{errors.comments}</div>}
                    <div className="tw-flex tw-items-start tw-justify-end tw-gap-4 tw-pb-8">
                        <TextFormField
                            name="reviewer_name"
                            placeholder="Enter your name"
                            required={true}
                            disabled={isSubmitting || budgetsDataRequest.loading || hasAnyReviewedActions}
                            hideLabel
                        />
                        <Button
                            variant="primary"
                            onClick={handleSubmit}
                            loading={isSubmitting}
                            disabled={!isValid || isSubmitting || budgetsDataRequest.loading}
                        >
                            Submit Review
                        </Button>
                    </div>
                </div>
            )}
        </Formik>
    );
};

export default OnlineReview;

if (document.getElementById('DepartmentsBudgetsOnlineReviewApp')) {
    const rootEl = document.getElementById('DepartmentsBudgetsOnlineReviewApp');

    // Data attributes
    const reviewDataUrl = rootEl.getAttribute('data-review-data-url');
    const reviewUpdateUrl = rootEl.getAttribute('data-review-update-url');
    const hasAnyReviewedActions = rootEl.getAttribute('data-has-any-reviewed-actions') === 'true';

    ReactDOM.render(
        <ToastProvider>
            <OnlineReview reviewDataUrl={reviewDataUrl} reviewUpdateUrl={reviewUpdateUrl} hasAnyReviewedActions={hasAnyReviewedActions} />
        </ToastProvider>,
        rootEl,
    );
}
